import { type ReactNode, useEffect, useState } from 'react';

import { FOOTER_ATTRIBUTION } from '@trinidad/shared/constants';

import { Logo } from '@/components/brand-logo';
import {
  PANELIST_SESSION_CHANGED_EVENT,
  clearPanelistSession,
  readStoredPanelistSession,
} from '@/lib/panelist-session-storage';

export type DesignNavActive = 'about' | 'faq' | 'home' | 'how' | 'legal' | 'login' | 'rewards';

export function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

function usePanelistSessionPresence(): boolean {
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    function syncSession() {
      setHasSession(Boolean(readStoredPanelistSession()));
    }

    syncSession();
    window.addEventListener(PANELIST_SESSION_CHANGED_EVENT, syncSession);
    window.addEventListener('storage', syncSession);

    return () => {
      window.removeEventListener(PANELIST_SESSION_CHANGED_EVENT, syncSession);
      window.removeEventListener('storage', syncSession);
    };
  }, []);

  return hasSession;
}

export function WebsiteShell({
  active,
  children,
  title,
}: {
  active?: DesignNavActive | undefined;
  children: ReactNode;
  title: string;
}) {
  useDocumentTitle(title);

  return (
    <main className="website-design min-h-screen bg-background text-foreground">
      <DesignNav active={active} />
      {children}
      <DesignFooter />
    </main>
  );
}

export function DesignNav({
  active = 'home',
  onJoin,
}: {
  active?: DesignNavActive | undefined;
  onJoin?: (() => void) | undefined;
}) {
  const [open, setOpen] = useState(false);
  const hasPanelistSession = usePanelistSessionPresence();
  const rewardsHref = hasPanelistSession ? '/your-points#rewards' : '/rewards';
  const links = [
    { href: '/how-it-works', key: 'how' as const, label: 'How it works' },
    { href: rewardsHref, key: 'rewards' as const, label: 'Rewards' },
    { href: '/about', key: 'about' as const, label: 'About' },
    { href: '/faq', key: 'faq' as const, label: 'FAQ' },
  ];
  const homeHref = active === 'home' ? '#top' : '/';
  const joinHref = active === 'home' ? '#signup' : '/#signup';

  function join() {
    if (onJoin) {
      onJoin();
      return;
    }

    window.location.href = '/#signup';
  }

  return (
    <header className="design-nav">
      <div className="shell flex items-center justify-between py-3.5">
        <Logo
          accentClassName="text-[var(--coral)]"
          className="design-logo"
          href={homeHref}
          markClassName="h-10 w-10"
          wordmarkClassName="display-md text-[22px] leading-none"
        />

        <nav className="desktop-nav hidden items-center gap-7">
          {links.map((link) => (
            <a
              className={active === link.key ? 'text-[var(--coral)]' : 'text-[var(--ink)]'}
              href={link.href}
              key={link.key}
            >
              {link.label}
            </a>
          ))}
          {hasPanelistSession ? (
            <>
              <a className="btn btn-sm btn-ghost" href="/dashboard">
                Dashboard
              </a>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  clearPanelistSession();
                  window.location.assign('/login');
                }}
                type="button"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <a
                aria-current={active === 'login' ? 'page' : undefined}
                className="btn btn-sm btn-ghost"
                href="/login"
              >
                Log in
              </a>
              <a className="btn btn-sm btn-primary" href={joinHref} onClick={onJoin}>
                Join free
              </a>
            </>
          )}
        </nav>

        <button
          aria-expanded={open}
          aria-label="Open menu"
          className="mobile-menu-btn"
          onClick={() => setOpen((current) => !current)}
          type="button"
        >
          <span className={open ? 'translate-y-[6.5px] rotate-45' : undefined} />
          <span className={open ? 'opacity-0' : undefined} />
          <span className={open ? '-translate-y-[6.5px] -rotate-45' : undefined} />
        </button>
      </div>

      <div className="mobile-drawer" data-open={open}>
        <div className="grid gap-1 px-5 pb-5 pt-3">
          {links.map((link) => (
            <a
              className={active === link.key ? 'text-[var(--coral)]' : undefined}
              href={link.href}
              key={link.key}
              onClick={() => setOpen(false)}
            >
              {link.label} <span aria-hidden="true">→</span>
            </a>
          ))}
          {hasPanelistSession ? (
            <div className="mt-3 flex gap-2">
              <a className="btn btn-ghost flex-1" href="/dashboard">
                Dashboard
              </a>
              <button
                className="btn btn-primary flex-1"
                onClick={() => {
                  clearPanelistSession();
                  window.location.assign('/login');
                }}
                type="button"
              >
                Sign out
              </button>
            </div>
          ) : (
            <div className="mt-3 flex gap-2">
              <a
                aria-current={active === 'login' ? 'page' : undefined}
                className="btn btn-ghost flex-1"
                href="/login"
              >
                Log in
              </a>
              <button
                className="btn btn-primary flex-1"
                onClick={() => {
                  setOpen(false);
                  join();
                }}
                type="button"
              >
                Join free
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export function DesignFooter() {
  const hasPanelistSession = usePanelistSessionPresence();
  const rewardsHref = hasPanelistSession ? '/your-points#rewards' : '/rewards';

  return (
    <footer className="design-footer">
      <div className="tt-stripe absolute left-0 right-0 top-0 h-3" />
      <div className="shell design-footer-grid">
        <div>
          <Logo
            accentClassName="text-[var(--coral)]"
            className="text-white"
            markClassName="h-9 w-9"
            wordmarkClassName="display-md text-2xl"
          />
          <p className="mt-3 max-w-sm text-sm leading-6 opacity-75">
            Trinidad &amp; Tobago&apos;s online panel. Your voice. Your rewards. Built locally for
            everyday Trinis &amp; Tobagonians.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
          <FooterCol
            links={[
              { href: '/how-it-works', label: 'How it works' },
              { href: rewardsHref, label: 'Rewards' },
              { href: '/faq', label: 'FAQ' },
              { href: '/#signup', label: 'Join free' },
            ]}
            title="Panel"
          />
          <FooterCol
            links={[
              { href: '/privacy', label: 'Privacy Policy' },
              { href: '/terms', label: 'Terms of Service' },
              { href: '/cookie-policy', label: 'Cookie Policy' },
              { href: 'mailto:hello@trinirewards.com', label: 'Contact' },
            ]}
            title="Legal"
          />
          <FooterCol
            links={[
              {
                href: 'https://cmr-panel.com/',
                label: 'CMR Panel, our Trinidad and Tobago research panel',
              },
              {
                href: 'https://caribbeanmarketresearch.com/',
                label: 'Caribbean Market Research, our parent firm since 1970',
              },
            ]}
            title="CMR network"
          />
        </div>
      </div>
      <div className="shell design-footer-bottom">
        <span>© 2026 Trini Rewards · Made in T&amp;T · Airtime powered by Ding</span>
        <span>{FOOTER_ATTRIBUTION}</span>
      </div>
    </footer>
  );
}

function FooterCol({
  links,
  title,
}: {
  links: Array<{ href: string; label: string }>;
  title: string;
}) {
  return (
    <div>
      <div className="display-md mb-3 text-sm uppercase tracking-[0.14em] text-white">{title}</div>
      <ul className="m-0 grid list-none gap-2 p-0">
        {links.map((link) => (
          <li key={link.label}>
            <a className="text-sm text-white no-underline opacity-90" href={link.href}>
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SectionHead({
  dark,
  eyebrow,
  title,
}: {
  dark?: boolean;
  eyebrow: string;
  title: ReactNode;
}) {
  return (
    <div className="mb-3.5">
      <div
        className="design-eyebrow"
        style={{
          color: dark ? 'var(--sun)' : 'var(--coral)',
        }}
      >
        {eyebrow}
      </div>
      <h2
        className="display mt-2 text-[clamp(34px,6vw,60px)]"
        style={{
          color: dark ? '#fff' : 'var(--ink)',
        }}
      >
        {title}
      </h2>
    </div>
  );
}

export function RedPeriod() {
  return (
    <span aria-hidden="true" className="ink-band-period">
      .
    </span>
  );
}

export function BullseyeDevice() {
  return (
    <div aria-hidden="true" className="ink-band-bullseye" data-bullseye-device="true">
      <span />
      <span />
      <span />
      <span />
    </div>
  );
}

export function BullseyeMarker({ action = false }: { action?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`bullseye-marker ${action ? 'bullseye-marker-action' : ''}`.trim()}
    />
  );
}

export function PageHero({
  children,
  eyebrow,
  title,
}: {
  children: ReactNode;
  eyebrow: string;
  title: ReactNode;
}) {
  return (
    <section className="design-page-hero">
      <div className="dot-fade" />
      <div className="shell relative text-center">
        <div className="design-eyebrow mb-[18px] text-[var(--coral)]">{eyebrow}</div>
        <h1 className="display mx-auto max-w-[880px] text-[clamp(40px,7vw,76px)]">{title}</h1>
        <div className="mx-auto mt-5 max-w-[580px] text-[17px] leading-normal text-[#1f2a37]">
          {children}
        </div>
      </div>
    </section>
  );
}

export function TopicIcon({ color, kind }: { color: string; kind: string }) {
  const stroke = 'currentColor';
  const strokeWidth = 2.4;
  const common = { fill: 'none', stroke, strokeLinecap: 'round' as const, strokeWidth };
  const iconProps = {
    height: 36,
    viewBox: '0 0 40 40',
    width: 36,
  };

  if (kind === 'restaurant') {
    return (
      <svg aria-hidden="true" {...iconProps}>
        <path d="M11 8 L 11 20 Q 11 22, 13 22 L 13 32" {...common} />
        <path d="M9 8 L 9 14 M 13 8 L 13 14" {...common} />
        <path d="M28 8 Q 24 8, 24 16 Q 24 20, 28 20 L 28 32" {...common} fill={color} />
      </svg>
    );
  }

  if (kind === 'food') {
    return (
      <svg aria-hidden="true" {...iconProps}>
        <circle cx="20" cy="22" fill={color} r="11" stroke={stroke} strokeWidth={strokeWidth} />
        <path d="M14 16 L 14 8 M 20 16 L 20 6 M 26 16 L 26 8" {...common} />
      </svg>
    );
  }

  if (kind === 'music') {
    return (
      <svg aria-hidden="true" {...iconProps}>
        <circle cx="14" cy="28" fill={color} r="5" stroke={stroke} strokeWidth={strokeWidth} />
        <circle cx="28" cy="25" fill={color} r="5" stroke={stroke} strokeWidth={strokeWidth} />
        <path d="M19 28 L 19 10 L 33 7 L 33 25" {...common} />
      </svg>
    );
  }

  if (kind === 'tv') {
    return (
      <svg aria-hidden="true" {...iconProps}>
        <rect
          fill={color}
          height="20"
          rx="4"
          stroke={stroke}
          strokeWidth={strokeWidth}
          width="28"
          x="6"
          y="12"
        />
        <path d="M14 8 L 20 12 L 26 8" {...common} />
      </svg>
    );
  }

  if (kind === 'travel') {
    return (
      <svg aria-hidden="true" {...iconProps}>
        <path
          d="M6 22 L 36 14 L 32 22 L 18 26 L 14 32 L 11 31 L 12 24 L 6 22 Z"
          fill={color}
          stroke={stroke}
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
        />
      </svg>
    );
  }

  if (kind === 'fashion') {
    return (
      <svg aria-hidden="true" {...iconProps}>
        <path
          d="M14 8 L 20 14 L 26 8 L 34 14 L 30 20 L 28 18 L 28 32 L 12 32 L 12 18 L 10 20 L 6 14 Z"
          fill={color}
          stroke={stroke}
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
        />
      </svg>
    );
  }

  if (kind === 'sports') {
    return (
      <svg aria-hidden="true" {...iconProps}>
        <circle cx="20" cy="20" fill={color} r="13" stroke={stroke} strokeWidth={strokeWidth} />
        <path d="M7 20 L 33 20 M 20 7 L 20 33 M 11 11 L 29 29 M 29 11 L 11 29" {...common} />
      </svg>
    );
  }

  if (kind === 'phone') {
    return (
      <svg aria-hidden="true" {...iconProps}>
        <rect
          fill={color}
          height="28"
          rx="3"
          stroke={stroke}
          strokeWidth={strokeWidth}
          width="14"
          x="13"
          y="6"
        />
        <circle cx="20" cy="29" fill={stroke} r="1.4" />
        <line x1="17" x2="23" y1="10" y2="10" {...common} />
      </svg>
    );
  }

  if (kind === 'tech') {
    return (
      <svg aria-hidden="true" {...iconProps}>
        <rect
          fill={color}
          height="18"
          rx="3"
          stroke={stroke}
          strokeWidth={strokeWidth}
          width="28"
          x="6"
          y="10"
        />
        <rect fill={stroke} height="3" width="12" x="14" y="30" />
      </svg>
    );
  }

  if (kind === 'beauty') {
    return (
      <svg aria-hidden="true" {...iconProps}>
        <circle cx="20" cy="22" fill={color} r="9" stroke={stroke} strokeWidth={strokeWidth} />
        <path
          d="M20 13 Q 14 6, 8 8 Q 12 14, 20 13 Z"
          fill={color}
          stroke={stroke}
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
        />
        <path
          d="M20 13 Q 26 6, 32 8 Q 28 14, 20 13 Z"
          fill={color}
          stroke={stroke}
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
        />
      </svg>
    );
  }

  if (kind === 'money') {
    return (
      <svg aria-hidden="true" {...iconProps}>
        <circle cx="20" cy="20" fill={color} r="13" stroke={stroke} strokeWidth={strokeWidth} />
        <path
          d="M20 12 L 20 28 M 24 16 Q 24 13, 20 13 Q 16 13, 16 16 Q 16 19, 20 20 Q 24 21, 24 24 Q 24 27, 20 27 Q 16 27, 16 24"
          {...common}
        />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" {...iconProps}>
      <path
        d="M6 20 L 20 8 L 34 20 L 34 32 L 24 32 L 24 22 L 16 22 L 16 32 L 6 32 Z"
        fill={color}
        stroke={stroke}
        strokeLinejoin="round"
        strokeWidth={strokeWidth}
      />
    </svg>
  );
}
